import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { useAuth } from '../../../contexts/AuthContext'
import { useCart } from '../../../hooks/useCart'
import { useIsMobile } from '../../../hooks/useIsMobile'
import {
  addToWishlist,
  addToWishlistLocal,
  removeFromWishlist,
  removeFromWishlistLocal,
  selectWishlistItems,
} from '../../../store/slices/wishlistSlice'
import { AppDispatch } from '../../../store/store'
import { Product, ProductPriceResponse } from '../../../types/product'
import {
  buildProductCartMeta,
  getEffectivePrice,
  getDiscountPercent,
  getProductDisplayWeight,
  hasCatalogDiscount,
} from '../../../utils/cartUtils'
import s from './ProductItem.module.css'

import { createPortal } from 'react-dom'
import Image from '../Image/Image'
import ProductMarkers from '../ProductMarkers/ProductMarkers'
import VariationWrapperPopup from '../VariationWrapperPopup/VariationWrapperPopup'

const getVariantWeightValue = (variant: ProductPriceResponse): number => {
  const rawWeight = variant.weight || variant.weightLabel || ''
  const match = rawWeight.match(/([\d.]+)\s*(kg|кг|g|г)?/i)
  if (!match) return Number.POSITIVE_INFINITY

  const value = Number(match[1])
  const unit = match[2]?.toLowerCase()
  return unit === 'kg' || unit === 'кг' ? value * 1000 : value
}

const getCardVariants = (prices: ProductPriceResponse[]): ProductPriceResponse[] => {
  return [...prices].sort((a, b) => {
    const priceDiff = getEffectivePrice(a) - getEffectivePrice(b)
    if (priceDiff !== 0) return priceDiff

    const weightDiff = getVariantWeightValue(a) - getVariantWeightValue(b)
    if (weightDiff !== 0) return weightDiff

    return (a.displayOrder ?? Infinity) - (b.displayOrder ?? Infinity)
  })
}

export default function ProductItem({ product }: { product: Product }) {
  const dispatch = useDispatch<AppDispatch>()
  const { addItem } = useCart()
  const { isAuthenticated } = useAuth()
  const [imageError, setImageError] = useState(false)

  const [isInCart, setIsInCart] = useState(false)
  const [isPopupOpen, setIsPopupOpen] = useState(false)
  const [quantity, setQuantity] = useState(1)

  const isMobile = useIsMobile()
  const wishlistItems = useSelector(selectWishlistItems)
  const isInWishlist = useMemo(
    () => wishlistItems.some(item => item.id === product.id),
    [wishlistItems, product.id],
  )

  // Автоматичне скидання стану isInCart через 5 секунд (тільки для десктопу)
  useEffect(() => {
    if (isInCart) {
      const timer = setTimeout(() => {
        setIsInCart(false)
      }, 5000)

      return () => clearTimeout(timer)
    }
  }, [isInCart])

  const cardVariants = getCardVariants(product.prices || [])
  const defaultVariant = cardVariants[0] ?? null
  const hasVariants = cardVariants.length > 0

  const effectivePrice = defaultVariant ? getEffectivePrice(defaultVariant) : null

  const getPriceDisplay = () => {
    if (!hasVariants) return 'Ціна не вказана'
    return `${effectivePrice} ₴`
  }

  const getOriginalPriceDisplay = () => {
    if (!defaultVariant || !hasCatalogDiscount(defaultVariant.discount)) return null
    return `${defaultVariant.price} ₴`
  }

  const getWeight = () => getProductDisplayWeight(product, defaultVariant)

  // Fallback зображення
  const getImageSrc = () => {
    if (imageError || !product.mainPicture) {
      return '/images/product.png' // Fallback зображення
    }
    return product.mainPicture
  }

  // Функції для зміни кількості
  const decreaseQuantity = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (quantity > 1) {
      setQuantity(quantity - 1)
    }
  }

  const increaseQuantity = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setQuantity(quantity + 1)
  }

  // Функція для роботи з вішлистом
  const handleWishlistToggle = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      if (isAuthenticated) {
        // For authenticated users, use API calls
        if (isInWishlist) {
          const result = await dispatch(removeFromWishlist(product.id) as any)
          if (removeFromWishlist.rejected.match(result)) {
            // Failed to remove from wishlist
          }
        } else {
          const result = await dispatch(addToWishlist({ productId: product.id, product }) as any)
          if (addToWishlist.rejected.match(result)) {
            // Failed to add to wishlist
          }
        }
      } else {
        // For unauthorized users, use local storage
        if (isInWishlist) {
          dispatch(removeFromWishlistLocal(product.id))
        } else {
          dispatch(addToWishlistLocal(product))
        }
      }
    } catch (error) {
      // Wishlist toggle error
    }
  }

  // Функція для додавання в кошик
  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (!hasVariants) return

    // On mobile, open the popup without adding to cart yet
    if (isMobile) {
      setIsPopupOpen(true)
      return
    }

    const cartItem = {
      id: parseInt(product.id) || 0,
      name: product.name,
      price: effectivePrice ?? 0,
      regular_price: defaultVariant?.price ?? 0,
      sale_price: hasCatalogDiscount(defaultVariant?.discount)
        ? (effectivePrice ?? undefined)
        : undefined,
      discount_percent: getDiscountPercent(defaultVariant?.discount),
      image: getImageSrc(),
      sku: product.id,
      quantity: quantity,
      chosenVariantId: defaultVariant?.id,
      variants: cardVariants,
      meta_data: buildProductCartMeta(product, { variant: defaultVariant }),
    }

    addItem(cartItem)
    setIsInCart(true)
  }

  return (
    <>
      <Link to={`/product/${product.slug}`} className={s.productItemLink}>
        <div className={`${s.productItem} ${isInCart ? s.inCart : ''}`}>
          <div
            className={s.wishlistToggle}
            onClick={handleWishlistToggle}
            role="button"
            tabIndex={0}
            aria-label={isInWishlist ? 'Прибрати з бажаного' : 'Додати до бажаного'}
            aria-pressed={isInWishlist}
          >
            <svg
              width="30"
              height="26"
              viewBox="0 0 30 26"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3.88302 14.6181L12.8231 24.0399C14.0061 25.2867 15.9926 25.2867 17.1756 24.04L26.1157 14.6181C29.0717 11.5028 29.0717 6.45189 26.1157 3.33659C23.1597 0.221297 18.3671 0.221299 15.4111 3.33659V3.33659C15.1873 3.57246 14.8114 3.57246 14.5876 3.33659V3.33659C11.6316 0.221299 6.83901 0.221298 3.88301 3.33659C0.927015 6.45189 0.927017 11.5028 3.88302 14.6181Z"
                stroke={isInWishlist ? '#FF7000' : '#BFBFBF'}
                strokeWidth="1.5"
                fill={isInWishlist ? '#FF7000' : 'none'}
              />
            </svg>
          </div>
          <div className={s.imageWrapper}>
            <Image
              mainPicture={imageError ? undefined : product.mainPicture}
              mainPictureBig={imageError ? undefined : product.mainPictureBig}
              alt=""
              onError={() => setImageError(true)}
            />

            {!isMobile && (
              <div className={s.manyInfo}>
                <ul className={s.manyInfoList}>
                  <li className={s.manyInfoItem}>
                    <span>Тип зерна</span>
                    <span>{product.beanType?.label || 'Не вказано'}</span>
                  </li>
                  {product.beanVariety?.label && (
                    <li className={s.manyInfoItem}>
                      <span>Різновид зерна</span>
                      <span>{product.beanVariety.label}</span>
                    </li>
                  )}
                  <li className={s.manyInfoItem}>
                    <span>Походження</span>
                    <span>{product.originCountry}</span>
                  </li>
                  <li className={s.manyInfoItem}>
                    <span>Регіон</span>
                    <span>{product.originRegion}</span>
                  </li>
                </ul>
              </div>
            )}
          </div>
          <div className={s.productInfo}>
            <ProductMarkers
              className={s.productMarker}
              relative={isMobile}
              tags={product.tags && Array.isArray(product.tags) ? product.tags : []}
              discount={defaultVariant?.discount}
            />
            <div className={s.topWrapper}>
              <div className={s.namingWrapper}>
                <h3 className={s.productName}>{product.name}</h3>
                <p className={s.productDescription}>{product.description}</p>
              </div>

              <div className={s.variationWrapper}>
                {product.variants && product.variants.length > 0 && (
                  <>
                    {product.variants.map((variant, index) => (
                      <div className={s.variation} key={index}>
                        <span className={s.variationTitle}>{variant.label}</span>
                        <span className={s.variationValue}>{variant.value}</span>
                      </div>
                    ))}
                  </>
                )}

                <div className={s.variation}>
                  <span className={s.variationTitle}>Кількість</span>
                  <div className={s.quantity}>
                    <button
                      type="button"
                      className={`${s.quantityButton} ${quantity <= 1 ? s.disabled : ''}`}
                      onClick={decreaseQuantity}
                      disabled={quantity <= 1}
                      aria-label="Зменшити кількість"
                    >
                      <svg
                        width="10"
                        height="2"
                        viewBox="0 0 10 2"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1.25 1L8.75 1"
                          stroke="#0D0D0D"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <span className={s.quantityValue}>{quantity}</span>
                    <button
                      type="button"
                      className={s.quantityButton}
                      onClick={increaseQuantity}
                      aria-label="Збільшити кількість"
                    >
                      <svg
                        width="30"
                        height="30"
                        viewBox="0 0 30 30"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M11.25 15H18.75"
                          stroke="#0D0D0D"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M15 11.2499L15 18.7499"
                          stroke="#0D0D0D"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className={s.infoWrapper}>
              <div className={s.priceWrapper}>
                {getOriginalPriceDisplay() ? (
                  <div className={s.priceDiscount}>
                    <p className={s.price}>{getPriceDisplay()}</p>
                    <p className={s.originalPrice}>{getOriginalPriceDisplay()}</p>
                  </div>
                ) : (
                  <p className={s.price}>{getPriceDisplay()}</p>
                )}
                {getWeight() && (
                  <p className={s.packagingWeight}>
                    / {getWeight()}
                  </p>
                )}
                {cardVariants.length > 1 && (
                  <span className={s.moreVariantsBadge}>+{cardVariants.length - 1}</span>
                )}
              </div>
              <div className={s.buttonsWrapper}>
                <button
                  className={`${s.button} ${isInCart ? s.done : ''}`}
                  onClick={handleAddToCart}
                  disabled={!hasVariants}
                >
                  {!isInCart && cartIcon()}
                  <span>{!isInCart ? 'В кошик' : addedIcon()}</span>
                </button>
                <button className={s.button}>
                  <span>Детальніше</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </Link>
      {isPopupOpen &&
        isMobile &&
        createPortal(
          <VariationWrapperPopup
            product={product}
            isOpen={isPopupOpen}
            onClose={() => setIsPopupOpen(false)}
            initialQuantity={quantity}
          />,
          document.body,
        )}
    </>
  )
}

export const doneIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
      <rect x="1" y="1" width="20" height="20" rx="10" stroke="white" strokeWidth="1.5" />
      <path
        d="M8 11H14"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11 7.99988L11 13.9999"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export const addedIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
      <rect width="20" height="20" rx="10" fill="white" />
      <path
        d="M13.0065 6.86366C13.2491 6.86243 13.4868 6.93466 13.6912 7.07168C13.8056 7.14807 13.8951 7.25922 13.9477 7.39031C14.0003 7.5214 14.0136 7.66621 13.9858 7.80543C13.8971 8.11412 13.7328 8.39302 13.5093 8.61429C13.0661 9.10656 12.6115 9.68562 12.2094 10.2161C11.6577 10.9438 11.0791 11.6344 10.5469 12.4095C10.1353 13.0091 9.45438 14.0308 8.84551 14.1262C8.16865 14.2322 7.6567 13.4878 7.32591 12.8728C6.85654 12.0698 6.43361 11.238 6.05941 10.3818C5.99018 10.1964 5.98119 9.99234 6.03383 9.80111C6.08647 9.60988 6.1978 9.44222 6.35055 9.32414C6.5033 9.20606 6.68893 9.14416 6.87864 9.14804C7.06835 9.15192 7.25154 9.22137 7.39978 9.34562C7.85647 9.73934 8.19898 10.4652 8.51749 10.9851C8.63326 11.1741 8.6506 11.2649 8.93634 10.9197C9.61236 10.1032 10.1855 9.44705 10.8725 8.64486C11.3322 8.10808 11.725 7.46177 12.3238 7.07861C12.5269 6.93953 12.764 6.86489 13.0065 6.86366Z"
        fill="#FF7000"
      />
    </svg>
  )
}

export const cartIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M12.0007 10.0002H4.66732L3.66732 4.00016H14.0007L12.0007 10.0002Z" fill="white" />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1.33398 2.00016C1.33398 1.63197 1.63246 1.3335 2.00065 1.3335H3.33398C3.65988 1.3335 3.938 1.5691 3.99158 1.89056L4.23207 3.3335H14.0007C14.2149 3.3335 14.4162 3.43651 14.5415 3.61036C14.6668 3.7842 14.7009 4.00768 14.6331 4.21098L12.6331 10.211C12.5424 10.4832 12.2876 10.6668 12.0007 10.6668H4.66732C4.34142 10.6668 4.0633 10.4312 4.00972 10.1098L2.76923 2.66683H2.00065C1.63246 2.66683 1.33398 2.36835 1.33398 2.00016ZM4.45429 4.66683L5.23207 9.3335H11.5201L13.0757 4.66683H4.45429Z"
        fill="white"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.00065 13.3335C4.00065 12.5971 4.5976 12.0002 5.33398 12.0002C6.07036 12.0002 6.66732 12.5971 6.66732 13.3335C6.66732 14.0699 6.07036 14.6668 5.33398 14.6668C4.5976 14.6668 4.00065 14.0699 4.00065 13.3335Z"
        fill="white"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.0007 13.3335C10.0007 12.5971 10.5976 12.0002 11.334 12.0002C12.0704 12.0002 12.6673 12.5971 12.6673 13.3335C12.6673 14.0699 12.0704 14.6668 11.334 14.6668C10.5976 14.6668 10.0007 14.0699 10.0007 13.3335Z"
        fill="white"
      />
    </svg>
  )
}
